/**
 * A record whose app is not here (RFC 0338 3g, S7).
 *
 * A record is a document, and a document is always reachable. When neither
 * its project nor the World has the app that draws its type, the pane says so
 * and offers what can be done: install the app it was made with, found in
 * another project, here or in the World; or read the fields as they are.
 * Nothing installs without a click.
 *
 * No "open with" a compatible app: an installed app owning this type would
 * have drawn the record, since one type has one owner (RFC 0338 S6).
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  type AppSummary,
  type ProjectDoc,
  projectApps,
  projectString,
  readDocumentData,
  scalarProjectEntry,
} from '@mod/core/model'
import { useMemo, useState } from 'react'
import { useDocument } from '../../services/world/repo/useDocument'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import { readWorldPointer } from '../../services/world/world'
import { useProjectDocs } from '../spaces/useProjectCatalogs'
import { instancesAreOpenable } from './resolveSubject'
import type { Subject } from './subject'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

export interface MissingAppCandidate {
  app: AppSummary
  projectId: string
  projectName: string
}

/**
 * Every DISTINCT app for `dataType` in the given projects, one row per app id,
 * named by the first project holding it.
 *
 * All of them rather than the first: S6 lets two projects each own the type
 * with different apps, and the record does not say which one made it, so the
 * person picks. `complete` is false while any project has not loaded, because
 * an unloaded catalog is not evidence that it holds nothing.
 */
export function findMissingApp(
  dataType: string,
  projects: ReadonlyArray<{ projectId: string; name: string; doc?: unknown }>,
): { candidates: MissingAppCandidate[]; complete: boolean } {
  const byId = new Map<string, MissingAppCandidate>()
  let complete = true
  for (const p of projects) {
    if (!p.doc) {
      complete = false
      continue
    }
    for (const a of projectApps(p.doc)) {
      if (a.dataType !== dataType || byId.has(a.id)) continue
      byId.set(a.id, { app: a, projectId: p.projectId, projectName: p.name })
    }
  }
  return { candidates: [...byId.values()], complete }
}

/**
 * Install an app's catalog entry, checked against every catalog at the moment
 * of writing (RFC 0338 S6's one owner per type).
 *
 * `here` copies the entry into the project, and refuses if the project or the
 * World has another owner of the type. `world` copies it into the World and
 * takes the same id out of every project, so no project copy shadows it; it
 * refuses if any catalog has another owner. Every catalog is loaded before
 * anything is read, and nothing awaits between the read and the writes.
 */
export async function installChecked(
  repo: Repo,
  opts: {
    appId: string
    from: string
    projectId: string
    worldId: string | null
    projectIds: readonly string[]
    where: 'here' | 'world'
  },
): Promise<void> {
  const { appId, from, projectId, worldId, where } = opts
  if (where === 'world' && !worldId) throw new Error('there is no World')
  const ids = [
    ...new Set([
      ...(worldId ? [worldId] : []),
      projectId,
      from,
      ...opts.projectIds,
    ]),
  ]
  const handles = new Map<string, Awaited<ReturnType<Repo['find']>>>()
  for (const id of ids) {
    const h = await repo.find<ProjectDoc>(id as AnyDocumentId)
    await (h as { whenReady?: () => Promise<void> }).whenReady?.()
    handles.set(id, h)
  }
  const appsOf = (id: string) =>
    ((handles.get(id)?.doc() as ProjectDoc | undefined)?.apps ?? {}) as Record<
      string,
      { dataType?: string; name?: string }
    >
  const entry = appsOf(from)[appId]
  if (!entry) throw new Error(`${appId} is no longer in that project`)
  const checked =
    where === 'here' ? [projectId, ...(worldId ? [worldId] : [])] : ids
  for (const id of checked) {
    for (const [other, ref] of Object.entries(appsOf(id))) {
      if (
        other !== appId &&
        entry.dataType &&
        projectString(ref?.dataType) === entry.dataType
      )
        throw new Error(
          `"${ref.name ?? other}" already owns "${entry.dataType}" there; two apps on one type cannot both draw it`,
        )
    }
  }
  // A project's catalog stores scalars (RFC 0357 S11); the World's is as it was.
  const plain = JSON.parse(JSON.stringify(entry))
  const copy = where === 'here' ? scalarProjectEntry(plain) : plain
  const target = where === 'here' ? projectId : (worldId as string)
  handles.get(target)?.change((d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[appId] = copy
  })
  if (where === 'world') {
    for (const id of ids) {
      if (id === worldId || !appsOf(id)[appId]) continue
      handles.get(id)?.change((d: Draft) => {
        if (d.apps) delete d.apps[appId]
      })
    }
  }
}

export function MissingAppView({
  file,
  projectId,
  dataType,
  onOpenApp,
}: {
  file: Subject
  projectId: string
  dataType: string
  /** Opens an app by id. An app that keeps its records in its own index does
   *  not draw one on its own, so installing it opens the app instead. */
  onOpenApp?: (appId: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const worldId = readWorldPointer()
  const projects = useWorldProjects()
  const others = useMemo(
    () => projects.filter((p) => p.projectId !== projectId),
    [projects, projectId],
  )
  const docs = useProjectDocs(
    useMemo(() => others.map((p) => p.projectId), [others]),
  )
  const { candidates, complete } = useMemo(
    () =>
      findMissingApp(
        dataType,
        others.map((p) => ({
          projectId: p.projectId,
          name: p.displayName,
          doc: docs.get(p.projectId),
        })),
      ),
    [dataType, others, docs],
  )
  const [record] = useDocument(file.id as AnyDocumentId)
  const fields = useMemo(() => readDocumentData(record) ?? {}, [record])
  const [showFields, setShowFields] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const install = (c: MissingAppCandidate, where: 'here' | 'world') => {
    if (busy) return
    setBusy(true)
    setError(null)
    installChecked(repo, {
      appId: c.app.id,
      from: c.projectId,
      projectId,
      worldId,
      projectIds: projects.map((p) => p.projectId),
      where,
    })
      .then(() => {
        // An openable record re-resolves and draws in this pane. One kept in
        // its app's index does not open on its own, so go to the app.
        if (!instancesAreOpenable(c.app)) onOpenApp?.(c.app.id)
      })
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false))
  }

  const one = candidates.length === 1 ? candidates[0] : undefined
  // The World only when it is certain no other app owns the type: every
  // project loaded and exactly one candidate.
  const worldOk = !!worldId && complete && !!one

  return (
    <div className="flex h-full flex-col items-center overflow-y-auto px-6 py-16">
      <div className="w-full max-w-md text-sm">
        <h2 className="mb-1 font-medium text-[15px] text-foreground">
          {file.title}
        </h2>
        <p className="mb-5 text-muted-foreground">
          {one
            ? `This is a ${one.app.name} record, and ${one.app.name} is not installed here.`
            : candidates.length > 1
              ? `More than one app draws "${dataType}" records. Pick the one this was made with.`
              : complete
                ? `No app here draws "${dataType}" records, and none was found in your other projects.`
                : `No app here draws "${dataType}" records. Looking in your other projects…`}
        </p>
        {candidates.map((c) => (
          <div key={c.app.id} className="mb-4">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => install(c, 'here')}
                className="rounded-md bg-foreground px-3 py-1.5 text-background disabled:opacity-50"
              >
                Install {c.app.name} here
              </button>
              {one && worldId ? (
                <button
                  type="button"
                  disabled={busy || !worldOk}
                  onClick={() => install(c, 'world')}
                  className="rounded-md border border-border px-3 py-1.5 text-foreground disabled:opacity-50"
                  title={
                    worldOk
                      ? `Every project gets ${c.app.name}`
                      : 'Waiting for your other projects to load'
                  }
                >
                  Install in the World
                </button>
              ) : null}
            </div>
            <p className="mt-1.5 text-muted-foreground text-xs">
              From {c.projectName}.
              {instancesAreOpenable(c.app)
                ? ''
                : ` ${c.app.name} keeps its records in its own list, so it opens there.`}
            </p>
          </div>
        ))}
        {error ? <p className="mb-5 text-red-500 text-xs">{error}</p> : null}
        <button
          type="button"
          onClick={() => setShowFields((v) => !v)}
          className="text-muted-foreground text-xs hover:text-foreground"
        >
          {showFields ? 'Hide its fields' : 'Read its fields'}
        </button>
        {showFields ? (
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border p-3 text-xs">
            {JSON.stringify(fields, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  )
}
